import {
  useCallback, useEffect, useState, useMemo
} from 'react';

import { useSearchParams, useNavigate } from 'react-router-dom';

import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';

import { FEDERATED } from '#client/build-env';
import { useDashboardPath } from '#client/hooks/useDashboardPath';

const SlideCheckout = () => {
  const navigate = useNavigate();
  const { get, priceListUuid } = usePriceList();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [servicePricingUuids, setServicePricingUuids] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [redirectPath, setRedirectPath] = useState('');
  const dashboardPath = useDashboardPath();

  const CHECKOUT_COMPLETE_REDIRECT_MAP = useMemo(() => ({
    business_kit: { path: dashboardPath, forceReload: false },
    corporate_docs: { path: dashboardPath, forceReload: false },
    standard_good_standing: { path: dashboardPath, forceReload: false },
    trademarks_search: { path: `${dashboardPath}/compliance/trademarks`, forceReload: true }
  }), [dashboardPath]);

  // Listen for the parameter "checkout" in the URL to open the dashboard checkout modal
  // filter must be in the following format: &filter={"interval":"annual","price":200}
  // no filter will default to annual pricing if there is more than one service pricing option
  useEffect(() => {
    const checkoutName = searchParams.get('checkout');
    const filter = searchParams.get('filter');
    if (priceListUuid && checkoutName) {
      const requestedServicePricing = get([checkoutName], filter ? JSON.parse(filter) : filter);

      const requestedServicePricingUuids = requestedServicePricing
        ?.map(({ pricing }) => pricing?.servicePricingUuid).flat();

      if (requestedServicePricingUuids?.length) {
        setIsCheckoutOpen(true);
        setServicePricingUuids(requestedServicePricingUuids);
        // Remove the checkout parameter from the URL
        setRedirectPath(
          CHECKOUT_COMPLETE_REDIRECT_MAP[checkoutName]
          || { path: dashboardPath, forceReload: false }
        );
        setSearchParams((params) => {
          params.delete('checkout');
          params.delete('filter');
          return params;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, priceListUuid]);

  const onCloseCheckout = useCallback(({ isPurchaseComplete }) => {
    setIsCheckoutOpen(false);
    if (isPurchaseComplete) {
      // Force reload only for trademarks_search redirect
      if (redirectPath.forceReload) {
        window.location.href = redirectPath.path;
      } else {
        navigate(redirectPath.path);
      }
    }
  }, [redirectPath, navigate]);

  return priceListUuid ? (
    <FederatedModule
      {...FEDERATED.EcommerceUi}
      exportName="SlideCheckout"
      isOpen={isCheckoutOpen}
      onClose={onCloseCheckout}
      priceListUuid={priceListUuid}
      servicePricingUuids={servicePricingUuids}
    />
  ) : null;
};

export default SlideCheckout;
